import { createSelector } from '@reduxjs/toolkit';
import { format, parse } from 'date-fns';
import { getSheetStartTimeDateFns } from 'helpers/_date-helpers';
import { sheetsAdapter } from './worklogsSlice';

// Grouping helpers: the [dateString, sheet[]] view is derived here, not stored in the reducer.

export function sortAndMapFieldTech(array, dateFormat) {
  const map = array.reduce((res, curr) => {
    if (!curr) return res;

    const date = curr?.createdAt
      ? format(new Date(curr.createdAt), dateFormat)
      : format(new Date(curr.startedAt), dateFormat);

    if (!res[date]) res[date] = [curr];
    else res[date].push(curr);

    return res;
  }, {});

  return Object.entries(map).sort(
    (a, b) =>
      parse(a[0], dateFormat, new Date()).getTime() - parse(b[0], dateFormat, new Date()).getTime()
  );
}

export function sortAndMap(array, backsort, shouldBeToday, dateFormat) {
  const map = array.reduce((res, curr) => {
    if (!curr) return res;

    const date = curr?.createdAt
      ? (curr.travelTimeOnly
          ? format(getSheetStartTimeDateFns(curr.startTimeDate || curr.createdAt, curr.startTime), dateFormat)
          : format(new Date(curr.hours?.start || curr.createdAt), dateFormat)
      ) || format(new Date(curr.createdAt), dateFormat)
      : format(new Date(curr.startedAt), dateFormat);

    if (!res[date]) res[date] = [curr];
    else res[date].push(curr);

    return res;
  }, {});

  if (shouldBeToday) {
    const today = format(new Date(), dateFormat);
    if (!map[today]) {
      map[today] = [null];
    }
  }
  if (backsort === 'reverse') {
    return Object.entries(map).sort(
      (a, b) =>
        parse(b[0], dateFormat, new Date()).getTime() -
        parse(a[0], dateFormat, new Date()).getTime()
    );
  }

  return Object.entries(map).sort(
    (a, b) =>
      parse(a[0], dateFormat, new Date()).getTime() - parse(b[0], dateFormat, new Date()).getTime()
  );
}

export const sortAndMapSubmitted = (array, dateFormat) => {
  const map = array.reduce((res, curr) => {
    if (!curr) return res;

    const date = curr?.hours?.start
      ? format(new Date(curr.hours.start), dateFormat)
      : format(new Date(curr.startTimeDate || curr.createdAt), dateFormat);

    if (!res[date]) res[date] = [curr];
    else res[date].push(curr);

    return res;
  }, {});

  return Object.entries(map).sort(
    (a, b) =>
      parse(b[0], dateFormat, new Date()).getTime() - parse(a[0], dateFormat, new Date()).getTime()
  );
};

const adapterSelectors = sheetsAdapter.getSelectors((state) => state.worklogs);
export const selectSheetEntities = adapterSelectors.selectEntities;

export const selectActiveIds = (state) => state.worklogs.activeIds;
export const selectSubmittedIds = (state) => state.worklogs.submittedIds;
export const selectForemanIds = (state) => state.worklogs.foremanIds;
export const selectDateFormat = (state) => state.personalProfile.organization?.settings?.dateFormat;
export const selectShouldBeToday = (state) => state.worklogs.shouldBeToday;

const mapIds = (ids, entities) => ids.map((id) => entities[id]).filter(Boolean);

export const selectActiveSheetsFlat = createSelector([selectActiveIds, selectSheetEntities], mapIds);
export const selectSubmittedSheetsFlat = createSelector([selectSubmittedIds, selectSheetEntities], mapIds);
export const selectForemanSheetsFlat = createSelector([selectForemanIds, selectSheetEntities], mapIds);

export const selectActiveWorklogs = createSelector(
  [selectActiveSheetsFlat, selectShouldBeToday, selectDateFormat],
  (flat, shouldBeToday, dateFormat) => sortAndMap(flat, 'reverse', shouldBeToday, dateFormat)
);

export const selectSubmittedWorklogs = createSelector(
  [selectSubmittedSheetsFlat, selectDateFormat],
  (flat, dateFormat) => sortAndMapSubmitted(flat, dateFormat)
);

export const selectForemanWorklogs = createSelector(
  [selectForemanSheetsFlat, selectDateFormat],
  (flat, dateFormat) => sortAndMap(flat, 'reverse', null, dateFormat)
);

export const selectSheetIds = (state) => state.worklogs.sheetIds;
export const selectActiveHasMore = (state) => state.worklogs.hasMore.active;
export const selectSubmittedHasMore = (state) => state.worklogs.hasMore.submitted;
export const selectForemanHasMore = (state) => state.worklogs.hasMore.foreman;
export const selectForceUpdate = (state) => state.worklogs.forceUpdate;
export const selectSheetsLength = (state) => state.worklogs.sheetsLength;
export const selectIsLoading = (state) => state.worklogs.isLoading;
export const selectError = (state) => state.worklogs.error;
